/**
 * Client-side bits of Trading Assessment — Checkout, step 1 (1:12250).
 *
 * The plan catalogue (steps, account types, sizes, prices) now comes from
 * `GET /v3/challenges/` via `useGetChallengesQuery`. What's left here is what the
 * API doesn't carry: the progress rail, the step glyphs, and the platform tiles.
 */

/** The two-node progress rail at the top of the card, 1:12259. */
export const checkoutSteps = [
  { n: 1, label: 'Step 1', sub: 'Select Plan' },
  { n: 2, label: 'Step 2', sub: 'Checkout' },
];

/**
 * Glyphs for the "Choose your Step" tiles, 1:12281. The API names its steps but
 * has no icon field, so they're matched by position — the design draws three
 * tiles and the catalogue returns three steps.
 */
export const stepIcons = ['assess-target', 'assess-stack', 'assess-crown'];

/**
 * "Platform" — 1:12449. Not part of the challenges API at all, so it stays
 * local. `logoWidth` is the logo's own box inside the tile; the tile itself is
 * sized by the row's grid rather than pinned to the frame's fixed widths, which
 * clipped the wider wordmarks.
 */
export const platforms = [
  { id: 'mt5', label: 'MetaTrader 5', short: 'MT5', logo: '/images/platforms/metatrader5.svg', logoWidth: 111.55 },
  { id: 'ctrader', label: 'cTrader', short: 'cTrader', logo: '/images/platforms/ctrader.svg', logoWidth: 68.23 },
  { id: 'dxtrade', label: 'DX Trade', short: 'DX Trade', logo: '/images/platforms/dxtrade.svg', logoWidth: 100.77 },
  { id: 'tradelocker', label: 'Trade Locker', short: 'Trade Locker', logo: '/images/platforms/tradelocker.svg', logoWidth: 110.65 },
  {
    id: 'alphatrader',
    label: 'Alpha Trader',
    short: 'Alpha Trader',
    logo: '/images/platforms/alphatrader.svg',
    logoWidth: 130.64,
    // The old checkout ships this tile with `disabled: true` — Alpha's own
    // platform isn't accepting accounts yet, so it renders but can't be picked.
    disabled: true,
    // The only tile the design frames in the spectrum gradient — it's Alpha's
    // own platform, so the frame is branding, not a selected state.
    branded: true,
  },
];

/**
 * "Payment Method" — step 2. One card provider and crypto, as the live
 * checkout offers them (N03): the card provider is the back end's call, not the
 * trader's. The live dashboard checkout calls `check/country/` and then always
 * selects Nuvei — its `paymentMethods` export has Checkout commented out — and
 * the back end currently answers a Checkout order with "Checkout payment
 * method is temporarily unavailable".
 *
 * No routed page on `main` offers Checkout — public and dashboard checkouts
 * both render UpdatedCheckoutPaymentPage. The only country routing left is in
 * the unrouted UpdatedPublicCheckout (Checkout instead of Nuvei for FR, ES, ZA,
 * SE, AE, SG, RO and MY): if Checkout comes back, that is the rule to restore,
 * keyed on `check/country/`.
 *
 * Nuvei's note lists the brands the live tile shows. CoinPayments' lists what
 * the processor actually accepts — USDT, USDC and ETH, no BTC or LTC — the same
 * coins its warning dialog names (TI review N04), and the name is one word.
 */
export const paymentMethods = [
  { id: 'nuvei', label: 'Nuvei', note: 'Mastercard, Visa, Apple Pay, Google Pay' },
  { id: 'coinspayment', label: 'CoinPayments', note: 'USDT, USDC, ETH' },
];

/**
 * "Spread" — step 2. Maps to the old checkout's `raw_spread` flag: Raw is index
 * 0, Standard index 1, and only Raw sets the flag. Raw is unavailable while the
 * Swap-Free add-on is on.
 */
export const spreads = [
  { id: 'raw', label: 'Raw spread' },
  { id: 'standard', label: 'Standard spread' },
];

/**
 * "EA Use" — step 1, under Platform, because picking MetaTrader is what raises
 * the question. The old checkout asks MetaTrader buyers whether they intend to
 * trade with an Expert Advisor and posts the answer as `EAs`. It is a
 * declaration, not an add-on: `/v2/get/final-price/` doesn't take the field and
 * quotes the same figure either way.
 *
 * No is first and is the default, so the answer only changes for a trader who
 * says so — which is also what the old checkout puts on the wire today, where
 * an unset `EAs` is dropped from the body entirely.
 */
export const eaChoices = [
  { id: 'no', label: 'No', value: false },
  { id: 'yes', label: 'Yes', value: true },
];

/**
 * "Payout Type" — step 2. The old checkout's payout types: 1 is bi-weekly, 2 is
 * on demand. `value` is what goes on the wire as `payout_type`; `ondemand` is
 * what `/v2/get/final-price/` wants, and it only honours the literal string
 * "true" — `ondemand=1` prices as bi-weekly.
 *
 * Bi-weekly leads the row and on demand is the default, the same way Spread
 * lists Raw first and defaults to Standard.
 */
export const payoutTypes = [
  { id: 'biweekly', label: 'Bi-Weekly', value: 1 },
  { id: 'ondemand', label: 'On-demand', value: 2 },
];

/**
 * "Add-ons" — step 2, ported from the old checkout's `addonsList`. Each carries
 * the conditions that decide whether it can be offered at all:
 *
 * - 90% Profit Share isn't a flag but a profit-split switch (80 ↔ 90), and only
 *   appears where the plan supports it — see `showProfitSplit` in the page.
 * - Swap-Free is MetaTrader-5 only, not offered on Swing or Alpha Direct, and
 *   disabled while EA Use is "Yes".
 *
 * The whole section disappears on Free Trial and Alpha Direct.
 */
export const addOns = [
  { id: 'ninety-split', label: '90% Profit Share', kind: 'profit-split' },
  { id: 'swap-free', label: 'Swap-Free Account', kind: 'swap-free' },
];

/**
 * The consents the old checkout requires under its Order Summary before
 * "Continue To Payment" — AC1-I92. Every one shown is mandatory, and the old page
 * blocks payment with one message for the set rather than marking each row.
 *
 * `links` renders inline, comma-separated, and an entry with no `href` renders
 * as plain text: the marketing site publishes /terms-and-conditions and
 * /privacy-policy, but the General Service Agreement and Trading terms have no
 * public URL to point at. Filling those two in here turns them into links with
 * no change to the page.
 *
 * `hiddenOn`, where present, lists the platform ids a consent doesn't apply to,
 * and `hiddenFor` the `check/country/` codes. The US-person declaration is not
 * asked on DX Trade or TradeLocker (AC1-I110), nor of a client in the US. It is
 * asked on cTrader again — cTrader is closed to new US registrations (review
 * doc, Checkout page), so it takes the declaration like MetaTrader. A consent
 * with neither applies everywhere.
 */
/**
 * The Terms and Conditions the checkout embeds under step 1 and links from
 * its consent. Served from `public/` rather than imported: the viewer's iframe
 * and the consent link both need one stable URL, and a PDF is a document to
 * open, not an asset to fingerprint into the bundle. The file name has spaces,
 * so it goes through `encodeURI` to be a valid URL.
 */
export const TERMS_PDF = encodeURI('/docs/Alpha Capital Group - Terms and Conditions.pdf');

export const consents = [
  {
    id: 'terms',
    lead: 'I declare that I have read and agree with',
    links: [
      { label: 'Terms & Conditions', href: TERMS_PDF },
      { label: 'Privacy Policy', href: TERMS_PDF  },
      { label: 'General Service Agreement',href: TERMS_PDF  },
      { label: 'Trading terms and conditions',href: TERMS_PDF  },
    ],
  },
  {
    id: 'us-person',
    lead: "I'm not a US resident or citizen.",
    hiddenOn: ['dxtrade', 'tradelocker'],
    hiddenFor: ['US'],
  },
  {
    id: 'identity',
    lead:
      'I acknowledge that signing up with personal details that do not match my ' +
      'ID/Personal information will lead to suspension of my payout(s) and closure ' +
      'of my account(s).',
  },
];

/** What the old checkout says when any of them is still unticked. */
export const CONSENT_REQUIRED = 'Please accept the terms and conditions';

/** Plans that never show add-ons, from the old checkout's guards. */
export const NO_ADDON_PLANS = ['free trial', 'alpha direct'];

/** Plans that can't take the Swap-Free add-on even on MT5. */
export const NO_SWAP_FREE_PLANS = ['swing', 'alpha direct'];

/** Plans that aren't asked EA Use even on MT5 — AC1-I111. */
export const NO_EA_PLANS = ['alpha direct'];
